import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Folder, Plus, Trash2, FileText, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Math } from "@/components/Math";

interface LibraryGroup {
  id: string;
  name: string;
  created_at: string;
}

interface LibraryItem {
  id: string;
  group_id: string | null;
  result_type: string;
  query: string;
  solution: string;
  created_at: string;
}

export default function Library() {
  const { user } = useAuth();
  const { role, isLoading: roleLoading } = useUserRole();
  const [groups, setGroups] = useState<LibraryGroup[]>([]);
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  const hasAccess = role === 'student' || role === 'paid' || role === 'admin';

  useEffect(() => {
    if (user && hasAccess) {
      fetchGroups();
      fetchItems();
    }
  }, [user, hasAccess]);

  const fetchGroups = async () => {
    const { data, error } = await supabase
      .from('library_groups')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching groups:', error);
      toast.error('Failed to load groups');
    } else {
      setGroups(data || []);
    }
  };

  const fetchItems = async () => {
    const { data, error } = await supabase
      .from('library_items')
      .select('*')
      .order('created_at', { ascending: false});

    if (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to load library items');
    } else {
      setItems(data || []);
    }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    const { error } = await supabase
      .from('library_groups')
      .insert({ name: newGroupName, user_id: user!.id });

    if (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group');
    } else {
      toast.success('Group created successfully');
      setNewGroupName("");
      setIsCreatingGroup(false);
      fetchGroups();
    }
  };

  const deleteGroup = async (groupId: string) => {
    const { error } = await supabase
      .from('library_groups')
      .delete()
      .eq('id', groupId);

    if (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    } else {
      toast.success('Group deleted successfully');
      fetchGroups();
      fetchItems();
    }
  };

  const deleteItem = async (itemId: string) => {
    const { error } = await supabase
      .from('library_items')
      .delete()
      .eq('id', itemId);

    if (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    } else {
      toast.success('Item deleted successfully');
      fetchItems();
    }
  };

  const moveItemToGroup = async (itemId: string, groupId: string | null) => {
    const { error } = await supabase
      .from('library_items')
      .update({ group_id: groupId })
      .eq('id', itemId);

    if (error) {
      console.error('Error moving item:', error);
      toast.error('Failed to move item');
    } else {
      toast.success('Item moved successfully');
      fetchItems();
    }
  };

  const filteredItems = selectedGroup 
    ? items.filter(item => item.group_id === selectedGroup)
    : items.filter(item => !item.group_id);

  if (roleLoading) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">Library</h1>
            <p className="text-muted-foreground">Loading...</p>
          </div>
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!user) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">Library</h1>
            <p className="text-muted-foreground">Save and organize your calculation results</p>
          </div>
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Sign In Required
              </CardTitle>
              <CardDescription>
                Please sign in to access your library
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full">
                <Link to="/auth">Sign In</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    );
  }

  if (!hasAccess) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">Library</h1>
            <p className="text-muted-foreground">Save and organize your calculation results</p>
          </div>
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Premium Feature
              </CardTitle>
              <CardDescription>
                The Library is available for Student and Paid members
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Upgrade your account to save and organize your calculation results in groups.
              </p>
              <Button asChild className="w-full">
                <Link to="/membership">View Membership Plans</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Library</h1>
          <p className="text-muted-foreground">Save and organize your calculation results</p>
        </div>
        
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Folder className="h-5 w-5" />
                    Groups
                  </CardTitle>
                  <CardDescription>Organize your saved results into groups</CardDescription>
                </div>
                <Dialog open={isCreatingGroup} onOpenChange={setIsCreatingGroup}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="gap-2">
                      <Plus className="h-4 w-4" />
                      New Group
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Group</DialogTitle>
                      <DialogDescription>
                        Give your group a name to organize your results
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <Input
                        placeholder="Group name"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && createGroup()}
                      />
                      <Button onClick={createGroup} className="w-full">
                        Create Group
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedGroup === null ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedGroup(null)}
                >
                  Ungrouped ({items.filter(i => !i.group_id).length})
                </Button>
                {groups.map((group) => (
                  <div key={group.id} className="flex items-center gap-1">
                    <Button
                      variant={selectedGroup === group.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedGroup(group.id)}
                    >
                      {group.name} ({items.filter(i => i.group_id === group.id).length})
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => deleteGroup(group.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Saved Results ({filteredItems.length})
              </CardTitle>
              <CardDescription>
                {selectedGroup 
                  ? `Results in ${groups.find(g => g.id === selectedGroup)?.name}`
                  : "Ungrouped results"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredItems.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No saved results yet. Start using SuperCalc and save your results!
                </p>
              ) : (
                <div className="space-y-4">
                  {filteredItems.map((item) => (
                    <Card key={item.id}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-base">
                              {item.result_type}
                            </CardTitle>
                            <CardDescription className="mt-1">
                              {new Date(item.created_at).toLocaleDateString()} at{' '}
                              {new Date(item.created_at).toLocaleTimeString()}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-2">
                            <Select
                              value={item.group_id || "ungrouped"}
                              onValueChange={(value) => 
                                moveItemToGroup(item.id, value === "ungrouped" ? null : value)
                              }
                            >
                              <SelectTrigger className="w-[150px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="ungrouped">Ungrouped</SelectItem>
                                {groups.map((group) => (
                                  <SelectItem key={group.id} value={group.id}>
                                    {group.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteItem(item.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <h4 className="font-semibold mb-2">Query:</h4>
                          <p className="text-sm text-muted-foreground">{item.query}</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">Solution:</h4>
                          <div className="prose dark:prose-invert max-w-none">
                            <Math>{item.solution}</Math>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
}
